/**
 * HTML Sanitization Utility
 *
 * Uses DOMPurify to sanitize user-generated HTML content before rendering.
 * This prevents XSS attacks when using dangerouslySetInnerHTML.
 */

// @ts-ignore - dompurify has no bundled type declarations
import DOMPurify from 'dompurify';

/**
 * Sanitize HTML content to prevent XSS attacks
 * @param dirty - The untrusted HTML string
 * @returns Sanitized HTML string safe for rendering
 */
export function sanitizeHtml(dirty: string | null | undefined): string {
 if (!dirty) return '';

 // Configure DOMPurify with safe defaults
 const clean = DOMPurify.sanitize(dirty, {
 // Allow common safe HTML tags
 ALLOWED_TAGS: [
 'p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'strike',
 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
 'ul', 'ol', 'li',
 'a', 'span', 'div',
 'blockquote', 'pre', 'code',
 'hr'
 ],
	 // Allow safe attributes only; avoid style/class/id for user-authored public content.
 ALLOWED_ATTR: [
	 'href', 'target', 'rel'
 ],
 // Force all links to open in new tab with security attributes
 ADD_ATTR: ['target', 'rel'],
 // Prevent javascript: URLs
 ALLOW_UNKNOWN_PROTOCOLS: false,
	 ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto):|[^a-z]|[a-z+.-]+(?:[^a-z+.-:]|$))/i,
 // Remove script tags and event handlers
 FORBID_TAGS: ['script', 'style', 'iframe', 'form', 'input', 'object', 'embed'],
 FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur']
 });

 return clean;
}

/**
 * Sanitize SVG content to prevent XSS attacks while preserving SVG elements
 * @param dirty - The untrusted SVG string
 * @returns Sanitized SVG string safe for rendering
 */
export function sanitizeSvg(dirty: string | null | undefined): string {
 if (!dirty) return '';

 // Use DOMPurify's SVG-safe mode
 const clean = DOMPurify.sanitize(dirty, {
 USE_PROFILES: { svg: true, svgFilters: true },
 // Remove dangerous SVG attributes
 FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur', 'xlink:href'],
 // Remove script and foreignObject which can embed HTML
 FORBID_TAGS: ['script', 'foreignObject']
 });

 return clean;
}

/**
 * Create a safe HTML object for React's dangerouslySetInnerHTML
 * @param dirty - The untrusted HTML string
 * @returns Object with sanitized __html property
 */
export function createSafeHtml(dirty: string | null | undefined): { __html: string } {
 return { __html: sanitizeHtml(dirty) };
}

/**
 * Hook transform to add security attributes to links
 */
if (typeof window !== 'undefined') {
 DOMPurify.addHook('afterSanitizeAttributes', (node: any) => {
 // Force all links to open in new tab safely
 if (node.tagName === 'A') {
 node.setAttribute('target', '_blank');
 node.setAttribute('rel', 'noopener noreferrer');
 }
 });
}

export default sanitizeHtml;

